// =====================================================================================
// INVRT Radar - web console - Chip (canonical mirror, Story PS1)
//
// Mirror of the INVRT Design System's Chip (components/Chip/Chip.jsx + Chip.d.ts): the
// five things product UI ever signals, a tinted pill whose fill is the hue at ~10% and
// whose text is the hue's darker ink, and an optional dot. Canonical signals map onto the
// app's EXISTING token pairings (zero new colours; see the pinned token debts):
//   buy   -> accent-50 / accent-700  (Compound Teal: growth, momentum achieved)
//   risk  -> red-50 / danger
//   pace  -> amber-50 / warning      (canonical pace is the amber family)
//   comp  -> primary-50 / primary-700 (canonical comp is a blue; the app's info pairing)
//   ctx   -> surface-muted / ink-soft (context: quiet, neutral)
// The canonical `comp` ink #0369A1 has no counterpart in this app's palette, and the
// canonical `info` violet is banned by the house "no purple anywhere" law, so both map to
// existing pairings rather than importing new hues.
// =====================================================================================

import type { HTMLAttributes } from "react";
import { cn } from "@/lib/utils";

export type ChipSignal = "buy" | "risk" | "pace" | "comp" | "ctx";

const SIGNAL_CLASSES: Record<ChipSignal, string> = {
  buy: "bg-accent-50 text-accent-700",
  risk: "bg-red-50 text-danger",
  pace: "bg-amber-50 text-warning",
  comp: "bg-primary-50 text-primary-700",
  ctx: "bg-surface-muted text-ink-soft",
};

export interface ChipProps extends HTMLAttributes<HTMLSpanElement> {
  signal?: ChipSignal | undefined;
  dot?: boolean;
  children?: React.ReactNode;
}

export function Chip({ signal, dot, children, className, ...rest }: ChipProps) {
  return (
    <span
      data-signal={signal}
      data-dot={dot ? "" : undefined}
      className={cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold",
        signal ? SIGNAL_CLASSES[signal] : "bg-surface-muted text-ink-soft",
        className,
      )}
      {...rest}
    >
      {dot ? <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-current" /> : null}
      {children}
    </span>
  );
}
